import { Injectable } from '@angular/core';
import { init as initApm } from '@elastic/apm-rum';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AnalyticsService } from '../analytics/analytics.service';


@Injectable({
  providedIn: 'root'
})
export class RumService {

  public apm: any;              // The apm instance that will be used throughout the app to instrument things
  private transaction: any;     // Will contain the current transaction being worked with.  Should only be one transaction active at a time.
  private userContext: object;  // Object representing the logged-in user.  Used for storing the user info with a log entry.
  private destroyed$: Subject<{}> = new Subject();       // For monitoring the destruction of the component.

  constructor( private auth: AuthService, private analytics: AnalyticsService ) {  
    this.apm = initApm(environment.apm_config);

    // Every time the user object changes, update the user context that is used to send out for logging
    this.auth.user.pipe( takeUntil( this.destroyed$ ) ) // Unsubscribe from the stream upon destruction.
    .subscribe( user => {
      if (user) {
        this.userContext = {
          id: user.uid,
          username: user.username,
          email: user.official_email
        };
      }
    });

    // Log all login / logout actions (note: this only really works on native app, web app refreshes page so event never fires)
    this.auth.authenticationState.pipe( takeUntil( this.destroyed$ ) ) // Unsubscribe from the stream upon destruction.
    .subscribe( isLoggedIn => {
      this.beginTransaction( isLoggedIn ? 'login' : 'logout', 'login');
      this.endTransaction();
    });
  }

  ngOnInit() { }

  ngOnDestroy() {
    this.destroyed$.next(); // Emit a notification on the subject that this component is destroyed.
  }

  /**
   * Called when app encounters an error.  The error is logged and any current transactions are cancelled.
   * @param error The error object to log.
   */
  logError(error) {
    this.apm.captureError(error);
    this.endTransaction();
  }

  /**
   * Logs an event to APM with an event type and a data context
   * @param actionName A name for this action
   * @param actionType A category for this action
   * @param context
   */
  beginTransaction(actionName: string, actionType: string, context?: object) {
    this.transaction = this.apm.startTransaction(actionName, actionType);
    if (this.userContext) {
      this.apm.setUserContext(this.userContext);
    }
    this.apm.setCustomContext(context);
    this.analytics.event(actionName, {
      event_category: actionType
    });
  }

  /** Ends a transaction if one is currently in effect */
  endTransaction() {
    if (this.transaction) {
      this.transaction.end();
    }
  }
}
